import { forwardRef } from 'react';

import { cx } from '@hrworks/design-system/functions';
import { useMediaQuery } from '@hrworks/design-system/hooks';

import { Tooltip } from '../Tooltip';
import { S } from './Badge.styles';
import type { BadgeProps } from './Badge.types';

export const Badge = forwardRef<HTMLElement, BadgeProps>(
  (
    {
      children,
      badgeContent,
      variant = 'filled',
      corner = 'pill',
      size = 'medium',
      dot,
      color = 'primary',
      width,
      className,
      ...otherProps
    }: BadgeProps,
    ref,
  ) => {
    const _mainAxisOffsetForTooltip = -26;
    const isTouchDevice = useMediaQuery('isTouchDevice');

    const badge = (
      <S.Badge
        className={cx(S.cls.Badge, className)}
        hasChildren={!!children}
        variant={variant}
        corner={corner}
        size={size}
        dot={dot}
        $color={color}
        $width={width}
        ref={ref}
        {...(dot &&
          isTouchDevice && {
            onClick: (e) => {
              e.stopPropagation();
              e.preventDefault();
            },
          })}
        {...otherProps}
      >
        {dot ? <S.ContentWrapper className={S.cls.ContentWrapper}>{badgeContent}</S.ContentWrapper> : badgeContent}
      </S.Badge>
    );

    if (dot && badgeContent && !children) {
      return (
        <Tooltip
          mainAxisOffset={_mainAxisOffsetForTooltip}
          trigger="hoverOrTouch"
          customTooltip={
            <Badge
              color={color}
              corner={corner}
              variant={variant}
              badgeContent={badgeContent}
              css={{ pointerEvents: 'none' }}
            />
          }
        >
          {badge}
        </Tooltip>
      );
    }

    return children && !badgeContent ? (
      children
    ) : children && badgeContent ? (
      <S.Container className={S.cls.Container} $width={width}>
        {children}
        {badge}
      </S.Container>
    ) : (
      badge
    );
  },
);
